// Shared bits for the WAAP Settings tabs. All settings tabs follow the DS
// settings recipe: `<Section>` + `<SettingRow>` + `<ActionBar>`. These helpers
// keep that consistent across the five tabs:
//   - Labeled   : a labelled field block (label + hint + control)
//   - ToggleRow : the DS SettingRow "Enable" toggle (Switch on the right)
//   - SaveBar   : the DS ActionBar save footer (inline, right-aligned)
import type { ReactNode } from 'react';
import { Button, Switch } from '@vnetwork/ui/primitives';
import { SettingRow, ActionBar } from '@vnetwork/ui/layout';
import styles from './SettingsUi.module.css';

/** A labelled field: label + optional required mark + hint + control. */
export function Labeled({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className={styles.labeled}>
      <div className={styles.labeledTitle}>
        {label} {required && <span className={styles.required}>*</span>}
      </div>
      {hint && <div className={styles.hint}>{hint}</div>}
      {children}
    </div>
  );
}

/**
 * A DS `<SettingRow>` toggle — title + description on the left, a `<Switch>` on
 * the right. Replaces the old bare `<label>` + `<Checkbox>` "Enable" rows so the
 * settings tabs match the app-wide SettingRow pattern (CDN settings, emergency).
 */
export function ToggleRow({
  title,
  description,
  checked,
  onChange,
  disabled,
}: {
  title: string;
  description?: ReactNode;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <SettingRow
      title={title}
      description={description}
      control={<Switch checked={checked} onCheckedChange={(v) => onChange(!!v)} disabled={disabled} />}
    />
  );
}

/** Right-aligned save footer — the DS `<ActionBar>` (inline, not sticky). */
export function SaveBar({
  onSave,
  saving,
  disabled,
  label = 'Save',
}: {
  onSave: () => void;
  saving?: boolean;
  disabled?: boolean;
  label?: string;
}) {
  return (
    <ActionBar
      sticky={false}
      end={
        <Button disabled={disabled || saving} onClick={onSave}>
          {saving ? 'Saving…' : label}
        </Button>
      }
    />
  );
}

/** Trigger a browser download of `content` as a UTF-8 text file named `name`. */
export function downloadText(name: string, content: string): void {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
